import type { AxiosError } from 'axios'
import type { HttpClient } from '../http/HttpClient'
import { ProductSaveError } from '@/domain/product/Product'
import type {
  CatalogItem,
  FixedAssetType,
  ProductDetail,
  ProductRepository,
  ProductSaveRequest,
  ProductsPage,
} from '@/domain/product/Product'

interface RawProduct {
  name: string
  number: string | null
  type: string
  unit: string | null
  unit_other: string | null
  content: string | null
  content_unit: string | null
  content_unit_other: string | null
  has_special_taxes: boolean
  is_fixed_asset: boolean | null
  fixed_asset_type: FixedAssetType | null
  society_ids?: number[]
}

interface RawProductResponse {
  product: RawProduct
  selected_categories: CatalogItem[]
  categories: CatalogItem[]
  selected_subcategories: CatalogItem[]
  subcategories: CatalogItem[]
}

export function toProductDetail(raw: RawProductResponse): ProductDetail {
  const { product } = raw
  return {
    name: product.name,
    number: product.number,
    type: product.type,
    unit: { code: product.unit, other: product.unit_other },
    content: product.content === null ? null : Number(product.content),
    contentUnit: { code: product.content_unit, other: product.content_unit_other },
    hasSpecialTaxes: product.has_special_taxes,
    isFixedAsset: product.is_fixed_asset || false,
    fixedAssetType: product.fixed_asset_type || null,
    selectedCategories: raw.selected_categories,
    categories: raw.categories,
    selectedSubcategories: raw.selected_subcategories,
    subcategories: raw.subcategories,
    societyIds: product.society_ids || [],
  }
}

function toRawRequest(request: ProductSaveRequest) {
  return {
    name: request.name,
    number: request.number,
    type: request.type,
    unit: request.unit,
    content: request.content,
    content_unit: request.contentUnit,
    has_special_taxes: request.hasSpecialTaxes,
    is_fixed_asset: request.isFixedAsset,
    fixed_asset_type: request.fixedAssetType,
    society_ids: request.societyIds,
    selected_categories: request.selectedCategories,
    selected_subcategories: request.selectedSubcategories,
  }
}

function toSaveError(error: unknown): ProductSaveError {
  const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail
  return new ProductSaveError(typeof detail === 'string' ? detail : '')
}

export class HttpProductRepository implements ProductRepository {
  constructor(private http: HttpClient) {}

  async get(id: number): Promise<ProductDetail> {
    return toProductDetail(await this.http.url(`purchases/products/${id}`).get<RawProductResponse>())
  }

  async create(request: ProductSaveRequest): Promise<ProductsPage> {
    try {
      return await this.http.url('purchases/products').body(toRawRequest(request)).post<ProductsPage>()
    } catch (error) {
      throw toSaveError(error)
    }
  }

  async update(id: number, request: ProductSaveRequest): Promise<ProductsPage> {
    try {
      return await this.http.url(`purchases/products/${id}`).body(toRawRequest(request)).put<ProductsPage>()
    } catch (error) {
      throw toSaveError(error)
    }
  }
}
